import { CSSProperties, ReactNode } from "react";
import { motion } from "framer-motion";
import { DUR, EASE, VIEWPORT } from "../../lib/motion";
import "./Section.css";

interface SectionProps {
    id: string;
    children: ReactNode;
    /** 1-based chapter number, rendered as 01, 02, … */
    index?: number;
    /** Short chapter name shown beside the number. */
    label?: string;
    /**
     * Leave the marker out of the page flow. Scroll scenes (story mode) draw
     * their own copy inside the pinned stage, where it can't collide with the
     * stage's title the way an in-flow marker would while the stage pins.
     */
    hideMarker?: boolean;
    /**
     * Give the section its own heartbeat glow. Used on the plain layout;
     * scroll scenes draw theirs inside the pinned stage instead.
     */
    beat?: boolean;
    /** "r, g, b" — the section's light colour. */
    tint?: string;
}

/**
 * `03 ── WORK ──────` — the chapter number, its name, and a hairline rule that
 * draws itself across as the chapter arrives.
 */
export function ChapterMarker({ index, label }: { index: number; label?: string }) {
    return (
        <div className="section-marker">
            <span className="section-num mono">{String(index).padStart(2, "0")}</span>
            {label && <span className="section-label mono">{label}</span>}
            <motion.span
                className="section-rule"
                aria-hidden="true"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={VIEWPORT}
                transition={{ duration: DUR.slow, ease: EASE }}
            />
        </div>
    );
}

/**
 * A chapter of the single-page document.
 *
 * This replaces the old scroll-driven book flip, which rotated every section up
 * to 65° on the X axis. That effect cost more than it earned: it forced a 3D
 * context around each section (blurring text mid-scroll), laid a
 * `mix-blend-mode: multiply` sheet over the content, nested badly with the
 * per-card tilt inside it, and left two of four sections edge-on at any given
 * moment. Readability is not a fair trade for a page-turn metaphor.
 *
 * What remains is a hairline rule that draws itself across as the chapter
 * arrives — the same "something is happening" signal, at a hundredth of the
 * cost, and it never touches the content's own transform.
 */
export default function Section({ id, children, index, label, hideMarker = false, beat = false, tint }: SectionProps) {
    return (
        <section id={id} className="section" style={tint ? ({ "--section-tint": tint } as CSSProperties) : undefined}>
            {beat && (
                <div className="section-heart-wrap" aria-hidden="true">
                    <div className="section-heart" />
                </div>
            )}
            {index !== undefined && !hideMarker && <ChapterMarker index={index} label={label} />}
            {children}
        </section>
    );
}
